package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import kotlin.math.max
import kotlin.math.min

// ─── TipTap (ProseMirror) bindings ────────────────────────────────────────
// Bundled through the @tiptap/* npm deps in build.gradle.kts (same route as
// firebase / marked). `Editor` is a named export of @tiptap/core; StarterKit
// and Highlight are default exports of their packages. `defaultExport` unwraps
// the ESM default the way Analytics.kt handles optional dynamic fields.

@JsModule("@tiptap/core")
@JsNonModule
private external object TiptapCore {
    class Editor(options: dynamic) {
        fun destroy()
    }
    object Extension {
        fun create(config: dynamic): dynamic
    }
}

@JsModule("@tiptap/pm/state")
@JsNonModule
private external object PmState {
    class Plugin(spec: dynamic)
    class PluginKey(name: String)
}

@JsModule("@tiptap/pm/view")
@JsNonModule
private external object PmView {
    val Decoration: dynamic
    val DecorationSet: dynamic
}

@JsModule("@tiptap/pm/model")
@JsNonModule
private external object PmModel {
    val Slice: dynamic
    val Fragment: dynamic
}

@JsModule("@tiptap/starter-kit")
@JsNonModule
private external object StarterKitModule

@JsModule("@tiptap/extension-highlight")
@JsNonModule
private external object HighlightModule

private fun defaultExport(module: Any): dynamic {
    val m = module.asDynamic()
    return if (m.default != null) m.default else m
}

/**
 * `styleIssue` mark — extends Highlight to carry two data attributes so a
 * flagged span knows its group (drives colour/method + the checkbox toggle via
 * CSS `[data-group]`) and its label (the hover hint via CSS `attr(data-label)`).
 * The parse/render closures are plain JS — no Kotlin capture — so `js(...)` is
 * the natural fit, matching the dynamic-object idiom in Analytics.kt. Real
 * per-issue spans aren't wired yet, but the mark ships ready for them.
 */
private fun styleIssueMark(): dynamic {
    val highlight = defaultExport(HighlightModule)
    val config = js("({})")
    config.addAttributes = js(
        "(function(){var p=(typeof this.parent==='function'&&this.parent())||{};" +
        "return Object.assign({},p,{" +
        "group:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-group');}," +
        "renderHTML:function(a){return a.group?{'data-group':a.group,'class':'wl-issue'}:{};}}," +
        "label:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-label');}," +
        "renderHTML:function(a){return a.label?{'data-label':a.label}:{};}}," +
        "title:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-title');}," +
        "renderHTML:function(a){return a.title?{'data-title':a.title}:{};}}," +
        "glabel:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-glabel');}," +
        "renderHTML:function(a){return a.glabel?{'data-glabel':a.glabel}:{};}}," +
        "kind:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-kind');}," +
        "renderHTML:function(a){return a.kind?{'data-kind':a.kind}:{};}}," +
        "itype:{default:null," +
        "parseHTML:function(e){return e.getAttribute('data-type');}," +
        "renderHTML:function(a){return a.itype?{'data-type':a.itype}:{};}}" +
        "});})"
    )
    // Overlapping violations nest: several styleIssue marks (different attrs)
    // may cover the same text. ProseMirror's default excludes a mark's own type.
    config.excludes = ""
    return highlight.extend(config)
}

/** A ProseMirror plugin that rejects every document-changing transaction, so
 *  the editor stays read-only while still allowing a caret / selection —
 *  which drives prev/next navigation. Selection-only transactions pass. */
private fun readOnlyCaretExtension(): dynamic {
    val spec = js("({})")
    spec.filterTransaction = js("(function(tr){ return !tr.docChanged; })")
    val plugin = PmState.Plugin(spec)
    val config = js("({ name: 'readOnlyCaret' })")
    config.addProseMirrorPlugins = { arrayOf(plugin) }
    return TiptapCore.Extension.create(config)
}

/** StarterKit cut down to what the reader holds: paragraphs, text, undo.
 *  Lists, headings, bold & co. would let typing «- » or «# » at a line start
 *  turn a paragraph into a list or a heading — the book is plain text. */
private fun plainStarterKit(): dynamic = defaultExport(StarterKitModule).configure(js(
    "({ blockquote: false, bold: false, bulletList: false, code: false, codeBlock: false," +
    " hardBreak: false, heading: false, horizontalRule: false, italic: false, listItem: false," +
    " listKeymap: false, link: false, orderedList: false, strike: false, underline: false," +
    " trailingNode: false })"
))

/**
 * ProseMirror plugin for the reader's paragraphs, built in plain JS (it lives
 * on ProseMirror's own state/view APIs):
 *  - a «not re-checked» bar on the top-level paragraphs listed by index
 *    (changed since the smart scan);
 *  - when editable: an «edited» bar from the start on the paragraphs listed
 *    in `edited` (a restored draft);
 *  - when editable: paragraphs touched by an edit get an «edited» bar, and an
 *    issue mark touched by an edit is removed over its whole extent — else a
 *    fixed phrase would stay highlighted and typed text would extend the mark
 *    (that removal is flagged `wlMarksOnly`, so it doesn't count as an edit
 *    and cascade onto neighbouring marks); `onChange` gets the number of
 *    edited paragraphs after every change.
 * No `$` anywhere in the JS: it would be a Kotlin string template.
 */
private val paragraphPluginFactory: dynamic = js(
    "(function (Plugin, PluginKey, Decoration, DecorationSet) {" +
    "  function changedRanges(trs) {" +
    "    var out = [];" +
    "    for (var t = 0; t < trs.length; t++) {" +
    "      var tr = trs[t];" +
    "      if (!tr.docChanged || tr.getMeta('wlMarksOnly')) continue;" +
    "      var maps = tr.mapping.maps;" +
    "      for (var i = 0; i < maps.length; i++) {" +
    "        maps[i].forEach(function (os, oe, ns, ne) {" +
    "          var a = ns, b = ne, j, u;" +
    "          for (j = i + 1; j < maps.length; j++) { a = maps[j].map(a, -1); b = maps[j].map(b, 1); }" +
    "          for (u = t + 1; u < trs.length; u++) { a = trs[u].mapping.map(a, -1); b = trs[u].mapping.map(b, 1); }" +
    "          out.push([a, b]);" +
    "        });" +
    "      }" +
    "    }" +
    "    return out;" +
    "  }" +
    "  function touchedParagraphs(doc, ranges) {" +
    "    var seen = {}, out = [], size = doc.content.size;" +
    "    function add(pos, node) { if (!seen[pos]) { seen[pos] = 1; out.push([pos, node]); } }" +
    "    ranges.forEach(function (r) {" +
    "      var a = Math.max(0, Math.min(r[0], size)), b = Math.max(a, Math.min(r[1], size));" +
    "      if (b > a) doc.nodesBetween(a, b, function (node, pos) {" +
    "        if (node.type.name === 'paragraph') { add(pos, node); return false; }" +
    "        return true;" +
    "      });" +
    "      var rp = doc.resolve(a);" +
    "      if (rp.depth >= 1) add(rp.before(1), rp.node(1));" +
    "    });" +
    "    return out;" +
    "  }" +
    "  function markRuns(para, start, mark) {" +
    "    var runs = [], cur = null;" +
    "    para.forEach(function (child, offset) {" +
    "      var from = start + offset, to = from + child.nodeSize;" +
    "      if (mark.isInSet(child.marks)) {" +
    "        if (cur && cur[1] === from) cur[1] = to; else { cur = [from, to]; runs.push(cur); }" +
    "      } else cur = null;" +
    "    });" +
    "    return runs;" +
    "  }" +
    "  function isEdited(spec) { return spec.edited === true; }" +
    "  function countEdited(doc, decos) {" +
    "    var n = 0, seen = {};" +
    "    decos.find(undefined, undefined, isEdited).forEach(function (d) {" +
    "      if (seen[d.from]) return;" +
    "      var node = doc.nodeAt(d.from);" +
    "      if (node && node.type.name === 'paragraph' && d.from + node.nodeSize === d.to) { seen[d.from] = 1; n++; }" +
    "    });" +
    "    return n;" +
    "  }" +
    "  return function (unchecked, edited, editable, onChange) {" +
    "    var key = new PluginKey('wlParagraphs');" +
    "    return new Plugin({" +
    "      key: key," +
    "      state: {" +
    "        init: function (config, state) {" +
    "          var want = {}, was = {}, decos = [], n = 0;" +
    "          for (var i = 0; i < unchecked.length; i++) want[unchecked[i]] = 1;" +
    "          for (var j = 0; j < edited.length; j++) was[edited[j]] = 1;" +
    "          state.doc.forEach(function (node, offset, index) {" +
    "            var to = offset + node.nodeSize;" +
    "            if (want[index]) decos.push(Decoration.node(offset, to, { class: 'wl-para-unchecked' }));" +
    "            if (editable && was[index]) { decos.push(Decoration.node(offset, to, { class: 'wl-para-edited' }, { edited: true })); n++; }" +
    "          });" +
    "          return { decos: DecorationSet.create(state.doc, decos), edited: n };" +
    "        }," +
    "        apply: function (tr, value) {" +
    "          if (!tr.docChanged) return value;" +
    "          var decos = value.decos.map(tr.mapping, tr.doc);" +
    "          if (!editable) return { decos: decos, edited: value.edited };" +
    "          var add = [];" +
    "          touchedParagraphs(tr.doc, changedRanges([tr])).forEach(function (pn) {" +
    "            var pos = pn[0], to = pos + pn[1].nodeSize;" +
    "            var has = decos.find(pos, to, isEdited).some(function (d) { return d.from === pos && d.to === to; });" +
    "            if (!has) add.push(Decoration.node(pos, to, { class: 'wl-para-edited' }, { edited: true }));" +
    "          });" +
    "          if (add.length) decos = decos.add(tr.doc, add);" +
    "          return { decos: decos, edited: countEdited(tr.doc, decos) };" +
    "        }" +
    "      }," +
    "      props: { decorations: function (state) { return key.getState(state).decos; } }," +
    "      appendTransaction: function (trs, oldState, newState) {" +
    "        if (!editable) return null;" +
    "        var ranges = changedRanges(trs);" +
    "        var type = newState.schema.marks.highlight;" +
    "        if (!ranges.length || !type) return null;" +
    "        var tr = newState.tr, removed = false;" +
    "        touchedParagraphs(newState.doc, ranges).forEach(function (pn) {" +
    "          var para = pn[1], start = pn[0] + 1, marks = [];" +
    "          para.forEach(function (child) {" +
    "            child.marks.forEach(function (m) {" +
    "              if (m.type === type && !marks.some(function (x) { return x.eq(m); })) marks.push(m);" +
    "            });" +
    "          });" +
    "          marks.forEach(function (m) {" +
    "            markRuns(para, start, m).forEach(function (run) {" +
    "              for (var k = 0; k < ranges.length; k++) {" +
    "                if (run[0] <= ranges[k][1] && run[1] >= ranges[k][0]) { tr.removeMark(run[0], run[1], m); removed = true; break; }" +
    "              }" +
    "            });" +
    "          });" +
    "        });" +
    "        return removed ? tr.setMeta('wlMarksOnly', true) : null;" +
    "      }," +
    "      view: function () {" +
    "        return { update: function (v, prev) {" +
    "          if (editable && v.state.doc !== prev.doc) onChange(key.getState(v.state).edited);" +
    "        } };" +
    "      }" +
    "    });" +
    "  };" +
    "})"
)(PmState.Plugin::class.js, PmState.PluginKey::class.js, PmView.Decoration, PmView.DecorationSet)

/** Paste in the editor as plain text: lines become paragraphs (blank ones
 *  skipped), formatting and our own <mark>s from a copied reader don't come in. */
private val plainPasteHandler: dynamic = js(
    "(function (Slice, Fragment) {" +
    "  return function (view, event) {" +
    "    var text = event.clipboardData ? event.clipboardData.getData('text/plain') : null;" +
    "    if (text == null) return false;" +
    "    var lines = text.replace(/\\r\\n?/g, '\\n').split('\\n')" +
    "      .map(function (l) { return l.trim(); }).filter(function (l) { return l !== ''; });" +
    "    if (!lines.length) return true;" +
    "    var state = view.state, tr;" +
    "    if (lines.length === 1) tr = state.tr.insertText(lines[0]);" +
    "    else {" +
    "      var schema = state.schema;" +
    "      var nodes = lines.map(function (l) { return schema.nodes.paragraph.create(null, schema.text(l)); });" +
    "      tr = state.tr.replaceSelection(new Slice(Fragment.fromArray(nodes), 1, 1));" +
    "    }" +
    "    view.dispatch(tr.scrollIntoView());" +
    "    return true;" +
    "  };" +
    "})"
)(PmModel.Slice, PmModel.Fragment)

/** Text of each top-level paragraph of [doc] — what a save sends; a heading
 *  gets its hidden marker back ([headingMarker]), an emptied one goes. */
private val docParagraphs: dynamic = js(
    "(function (doc) { var out = []; doc.forEach(function (node) {" +
    " var t = node.textContent, h = node.attrs ? node.attrs.heading : null;" +
    " out.push(h && t.trim() ? h + t : t); }); return out; })"
)

/** The hidden heading marker of a paragraph, as an attribute (`data-heading`,
 *  class `wl-ms-heading`). Enter at the end of a heading starts a plain
 *  paragraph: the attribute isn't carried over. */
private fun headingMarkerExtension(): dynamic = TiptapCore.Extension.create(js(
    "({ name: 'wlHeadingMarker', addGlobalAttributes: function () { return [{ types: ['paragraph'], attributes: {" +
    " heading: { default: null, keepOnSplit: false," +
    "  parseHTML: function (el) { return el.getAttribute('data-heading'); }," +
    "  renderHTML: function (a) { return a.heading == null ? {} : { 'data-heading': a.heading, 'class': 'wl-ms-heading' }; } }" +
    " } }]; } })"
))

private fun pluginExtension(name: String, plugin: dynamic): dynamic {
    val config = js("({})")
    config.name = name
    config.addProseMirrorPlugins = { arrayOf(plugin) }
    return TiptapCore.Extension.create(config)
}

/** Typing in a text that may not be edited yet: swallow the input and let
 *  the reader show what editing gives. Navigation keys and copying pass. */
private val lockedInputProps: dynamic = js(
    "(function (onLocked) {" +
    "  return {" +
    "    handleKeyDown: function (view, event) {" +
    "      if (event.ctrlKey || event.metaKey || event.altKey) return false;" +
    "      if (event.key && (event.key.length === 1 || event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Enter')) { onLocked(); return true; }" +
    "      return false;" +
    "    }," +
    "    handleTextInput: function () { onLocked(); return true; }," +
    "    handlePaste: function () { onLocked(); return true; }," +
    "    handleDrop: function () { return true; }" +
    "  };" +
    "})"
)

/** Build the manuscript editor rendering [html] inside [element]. `editable`
 *  stays on so a caret can be placed (navigation); when [canEdit] is off
 *  readOnlyCaretExtension blocks every change and typing calls [onLocked].
 *  When on: pastes come in as plain text, drops from outside are ignored, no
 *  input rule fires, [onEdited] gets the number of edited paragraphs after
 *  each change and [onFocus] fires when the text gets the caret.
 *  [uncheckedParas] — paragraph indices to bar as «not re-checked»,
 *  [editedParas] — as already edited (a restored draft).
 *  Spellcheck stays off: it would underline on top of the issue marks. */
private fun createManuscriptEditor(
    element: HTMLElement, html: String, canEdit: Boolean,
    uncheckedParas: List<Int>, editedParas: List<Int>,
    onEdited: (Int) -> Unit, onLocked: () -> Unit, onFocus: () -> Unit,
): TiptapCore.Editor {
    val options = js("({})")
    options.element = element
    options.content = html
    options.editable = true
    options.enableInputRules = false
    options.enablePasteRules = false
    val paragraphs = pluginExtension("wlParagraphs", paragraphPluginFactory(
        uncheckedParas.toTypedArray(), editedParas.toTypedArray(), canEdit, onEdited))
    options.extensions = if (canEdit) arrayOf(plainStarterKit(), headingMarkerExtension(), styleIssueMark(), paragraphs)
                         else arrayOf(plainStarterKit(), headingMarkerExtension(), styleIssueMark(), paragraphs,
                                      readOnlyCaretExtension())
    val props = if (canEdit) js("({})") else lockedInputProps(onLocked)
    props.attributes = js("({ spellcheck: 'false' })")
    if (canEdit) {
        props.handlePaste = plainPasteHandler
        props.handleDrop = js("(function (view, event, slice, moved) { return !moved; })")
        options.onFocus = { _: dynamic -> onFocus() }
    }
    options.editorProps = props
    return TiptapCore.Editor(options)
}

/** Scroll [box] — the editor's own scroll — so the caret of [editor] shows
 *  after the box changed size around it. */
private fun keepCaretVisible(editor: Any?, box: HTMLElement?) {
    val ed = editor?.asDynamic() ?: return
    if (box == null || ed.isFocused != true) return
    val c = ed.view.coordsAtPos(ed.state.selection.head)
    val r = box.getBoundingClientRect()
    val pad = 32.0
    val top = c.top.unsafeCast<Double>()
    val bottom = c.bottom.unsafeCast<Double>()
    if (bottom > r.bottom - pad) box.scrollTop += bottom - r.bottom + pad
    else if (top < r.top + pad) box.scrollTop -= r.top + pad - top
}

/** The page's scroll anchoring, held off while a save, re-check or cancel
 *  swaps the text and tool rows under the reader's tools: it would keep a
 *  line of the old text in place and push the tools off screen instead. */
private object PageAnchoring {
    private var holds = 0
    private fun root() = document.documentElement as? HTMLElement
    fun hold() {
        if (holds++ == 0) root()?.style?.setProperty("overflow-anchor", "none")
    }
    /** Back on once the swapped content is laid out. */
    fun release() {
        window.setTimeout({ if (--holds == 0) root()?.style?.removeProperty("overflow-anchor") }, 500)
    }
}

/**
 * Floating hint tooltip on `document.body`, driven by hover over `.wl-issue`
 * marks inside [container]. A body-level element (vs a CSS `::after`) escapes
 * the reader's `overflow` clipping, and its position is clamped to the
 * viewport so it never runs off an edge. Skips marks whose group is toggled
 * off. Returns a detach function.
 */
private fun attachHintTooltip(
    container: HTMLElement,
    selector: String = ".wl-issue[data-label]",
    isHidden: (HTMLElement) -> Boolean = { false },
): () -> Unit {
    val tip = document.createElement("div") as HTMLElement
    tip.className = "wl-hint-tip"
    tip.style.display = "none"
    document.body?.appendChild(tip)
    val vv = window.asDynamic().visualViewport
    var shownFor: HTMLElement? = null

    fun place(mark: HTMLElement) {
        val r = mark.getBoundingClientRect()
        val tw = tip.offsetWidth.toDouble()
        val th = tip.offsetHeight.toDouble()
        val left = max(8.0, min(r.left + r.width / 2 - tw / 2, window.innerWidth - tw - 8.0))
        // With a phone keyboard open only part of the page shows.
        val visibleTop = (vv?.offsetTop as? Double ?: 0.0) + 8.0
        val above = r.top - th - 8.0
        val top = if (above < visibleTop) r.bottom + 8.0 else above
        tip.style.left = "${left}px"
        tip.style.top = "${top}px"
    }

    val onOver: (Event) -> Unit = onOver@{ e ->
        // Overlapping violations are nested marks: list every visible one under
        // the pointer, innermost first.
        val chain = ArrayList<HTMLElement>()
        var cur = (e.target as? Element)?.closest(selector) as? HTMLElement
        while (cur != null && container.contains(cur)) {
            if (!isHidden(cur)) chain.add(cur)
            cur = cur.parentElement?.closest(selector) as? HTMLElement
        }
        val mark = chain.firstOrNull() ?: return@onOver
        // Optional header: data-glabel = violation group, data-title = the
        // violation itself; data-label = the detail body. Falls back to plain
        // body text when there's no title.
        val single = chain.size == 1 && mark.getAttribute("data-title").isNullOrEmpty()
        if (single) {
            tip.textContent = mark.getAttribute("data-label") ?: ""
        } else {
            tip.innerHTML = chain.joinToString("") { m ->
                val bodyText = m.getAttribute("data-label") ?: ""
                val title = m.getAttribute("data-title")
                val glabel = m.getAttribute("data-glabel")
                buildString {
                    append("<div class=\"wl-hint-item\">")
                    if (!glabel.isNullOrEmpty()) append("<div class=\"wl-hint-group\">${escapeHtml(glabel)}</div>")
                    if (!title.isNullOrEmpty()) append("<div class=\"wl-hint-title\">${escapeHtml(title)}</div>")
                    if (bodyText.isNotEmpty()) append("<div class=\"wl-hint-body\">${escapeHtml(bodyText)}</div>")
                    append("</div>")
                }
            }
        }
        tip.style.display = "block"
        tip.style.visibility = "visible"
        shownFor = mark
        place(mark)
    }
    val onOut: (Event) -> Unit = onOut@{ e ->
        if ((e.target as? Element)?.closest(selector) != null) tip.style.display = "none"
    }
    // Typing may remove the mark under the pointer — hide the hint with it.
    val onKey: (Event) -> Unit = { tip.style.display = "none" }
    // The text can move under a shown hint — on a phone the same tap opens the
    // keyboard (and the full screen): follow the mark; out of view, wait unseen.
    val onMove: (Event) -> Unit = {
        window.requestAnimationFrame {
            val m = shownFor
            if (m != null && tip.style.display != "none") {
                val r = m.getBoundingClientRect()
                val c = container.getBoundingClientRect()
                when {
                    !container.contains(m) -> tip.style.display = "none"
                    r.bottom > c.top && r.top < c.bottom -> { tip.style.visibility = "visible"; place(m) }
                    else -> tip.style.visibility = "hidden"
                }
            }
        }
    }
    container.addEventListener("mouseover", onOver)
    container.addEventListener("mouseout", onOut)
    container.addEventListener("keydown", onKey)
    container.addEventListener("scroll", onMove)
    vv?.addEventListener("resize", onMove)
    vv?.addEventListener("scroll", onMove)
    return {
        container.removeEventListener("mouseover", onOver)
        container.removeEventListener("mouseout", onOut)
        container.removeEventListener("keydown", onKey)
        container.removeEventListener("scroll", onMove)
        vv?.removeEventListener("resize", onMove)
        vv?.removeEventListener("scroll", onMove)
        tip.remove()
    }
}

// ─── Highlight groups ─────────────────────────────────────────────────────
// One group = one colour + one method (defined in index.html CSS, keyed by
// `id`). The MVP set from the analyzer's word-list checks; `label` is the
// legend text and the hover hint.
private class IssueGroup(val id: String, val label: String, val hint: String)

private fun issueGroups(s: Strings): List<IssueGroup> = listOf(
    IssueGroup("cliche", s.hlGroupCliche, s.hlHintCliche),
    IssueGroup("filter", s.hlGroupFilter, s.hlHintFilter),
    IssueGroup("filler", s.hlGroupFiller, s.hlHintFiller),
    IssueGroup("bureaucratese", s.hlGroupBureaucratese, s.hlHintBureaucratese),
    IssueGroup("began", s.hlGroupBegan, s.hlHintBegan),
    IssueGroup("tautology", s.hlGroupTautology, s.hlHintTautology),
    IssueGroup("weak_speech", s.hlGroupWeakSpeech, s.hlHintWeakSpeech),
    IssueGroup("adj_stack", s.hlGroupAdjStack, s.hlHintAdjStack),
    IssueGroup("author_stance", s.hlGroupAuthorStance, s.hlHintAuthorStance),
    IssueGroup("intensifier", s.hlGroupIntensifier, s.hlHintIntensifier),
    IssueGroup("weak_verb", s.hlGroupWeakVerb, s.hlHintWeakVerb),
    IssueGroup("passive", s.hlGroupPassive, s.hlHintPassive),
)

/** Reader scroll position between editor remounts. Plain field, not Compose
 *  state: it's written on every scroll event and read only on remount. */
private class ScrollMemory { var top = 0.0 }

/** Last next/prev stop, kept like [ScrollMemory]: written on each step, read on the next. */
private class NavMemory { var index = -1 }

private enum class HlMode { STATIC, AI }
private enum class AiStatus { IDLE, LOADING, LOADED, ERROR }

// Shared legend rows — one row per group (checkbox + colour swatch + label +
// count). Used by both the static and the AI legend.
@Composable
private fun LegendRows(groups: List<IssueGroup>, enabled: Set<String>,
                       counts: Map<String, Int>, onToggle: (String) -> Unit) {
    Div({ classes("wl-legend-rows") }) {
        groups.forEach { g ->
            Label(attrs = { classes("wl-legend-row") }) {
                CheckboxInput(checked = g.id in enabled) { onInput { onToggle(g.id) } }
                Span({ classes("wl-issue", "wl-issue-sample"); attr("data-group", g.id); attr("data-kind", "static") }) { Text("Аа") }
                Span({
                    classes("wl-legend-label")
                    attr("data-label", "${g.label} — ${g.hint}")
                    style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurface); property("cursor", "help") }
                }) { Text(g.label) }
                Span({ classes("wl-legend-count") }) { Text((counts[g.id] ?: 0).toString()) }
            }
        }
    }
}

// AI legend — a tree/accordion: each catalogue section expands to its violation
// types. A type's short label matches the per-span hover label, so a hinted type
// maps straight to a leaf (full definition rides on the leaf's own hover). The
// section has a TOGGLE that shows/hides the whole group; each type has its own
// checkbox. Clicking the section header (anywhere but the toggle) expands it.
@Composable
private fun AiLegendTree(
    sections: List<AiCatSection>,
    secCounts: Map<String, Int>,
    typeCounts: Map<String, Int>,
    isTypeOn: (String) -> Boolean,
    expanded: Set<String>,
    onToggleType: (String) -> Unit,
    onToggleSection: (AiCatSection) -> Unit,
    onExpand: (String) -> Unit,
    enabled: Boolean,
) {
    Div({ classes("wl-ai-tree"); if (!enabled) classes("wl-ai-disabled") }) {
        sections.forEach { sec ->
            val secOn = sec.types.all { isTypeOn(it.id) }
            Div({ classes("wl-ai-sec"); if (sec.id in expanded) classes("open") }) {
                Div({ classes("wl-ai-sec-head"); onClick { onExpand(sec.id) } }) {
                    Div({
                        classes("wl-ai-toggle"); if (secOn) classes("on")
                        onClick { it.stopPropagation(); onToggleSection(sec) }
                    })
                    Span({ classes("wl-issue", "wl-issue-sample"); attr("data-group", sec.id); attr("data-kind", "smart") }) { Text("Аа") }
                    Span({ classes("wl-ai-sec-label"); style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurface) } }) { Text(sec.label) }
                    Span({ classes("wl-legend-count") }) { Text((secCounts[sec.id] ?: 0).toString()) }
                    Span({ classes("wl-ai-caret") }) { Text("▸") }
                }
                Div({ classes("wl-ai-types") }) {
                    sec.types.forEach { t ->
                        val c = typeCounts[t.id] ?: 0
                        Label(attrs = { classes("wl-ai-type") }) {
                            CheckboxInput(checked = isTypeOn(t.id)) { onInput { onToggleType(t.id) } }
                            Span({
                                classes("wl-ai-type-label")
                                attr("data-label", "${t.label} — ${t.hint}")
                            }) { Text(t.label) }
                            Span({ classes("wl-ai-type-count"); if (c == 0) classes("zero") }) { Text(c.toString()) }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Manuscript reader — emitted as three children of the results grid
 * (`.wl-results-grid`): a full-width header, the legend in the LEFT column
 * (same track as the sidebar cards), and the text in the RIGHT column, with
 * the grid's `align-items: start` lining the legend up with the top of the
 * text field. On narrow (<1100px) they stack.
 *
 * The full manuscript text is fetched from the backend (the same `book.txt`
 * the .txt download serves) and rendered read-only. The legend's checkboxes
 * toggle which stylistic-issue groups are highlighted; real per-issue spans
 * aren't wired yet, so the text currently shows without highlights.
 */
/** What the reader needs to let the owner edit the book's text. */
class TextEditing(
    /** May edit (bought credits, or switched on in the office); else the tools are locked. */
    val canEdit: Boolean,
    /** When the text was last edited (ISO); null while it is the uploaded one. */
    val editedAt: String?,
    /** Uploaded file name — for the downloaded .txt. */
    val fileName: String?,
    /** The «what editing gives» card opened for someone who may not edit yet;
     *  [source] — what opened it: "recheck" or "typing". */
    val onLockedShown: (source: String) -> Unit,
    /** «Выбрать пакет» on that card: sign in first, else the top-up dialog. */
    val onChoosePack: () -> Unit,
    /** First change since the text was last saved. */
    val onStarted: () -> Unit,
    val onSaved: (res: TextEditOutcome.Saved, paragraphsChanged: Int) -> Unit,
    val onSaveFailed: (reason: String) -> Unit,
    val onDownloaded: () -> Unit,
    /** A re-check of the edited places finished (credits may have been spent). */
    val onRechecked: (cost: Int, removed: Int, added: Int) -> Unit,
    val onRecheckFailed: (reason: String) -> Unit,
)

/** The editor's mount key, kept outside Compose state: it may only change
 *  while the text holds no unsaved edits, so it's written during composition. */
private class KeyHolder { var key = "" }

/** Hidden legend groups/types, read by the hover hint at event time. */
private class HiddenMarks { var test: (HTMLElement) -> Boolean = { false } }

/** Unsaved reader edits of one book, kept in localStorage in case the tab
 *  closes or the save fails. One book at a time — a novel is megabytes.
 *  Tied to the account too: the book id is the file's fingerprint, and
 *  another account on this browser may open the same file. */
@kotlinx.serialization.Serializable
private data class EditDraft(
    val bookId: String, val baseSha: String, val paragraphs: List<String>, val userId: String = "",
)

private object EditDrafts {
    private const val KEY = AppDataStore.EDIT_DRAFT_KEY
    // The session can end mid-edit (a refresh gets invalid_grant and clears the
    // stored id) while the editor stays open: drafts go on under that account.
    private var lastUser: String? = null

    private fun user(): String? = (AppDataStore.getUserId() ?: lastUser)?.also { lastUser = it }

    fun load(bookId: String): EditDraft? = try {
        val user = AppDataStore.getUserId()
        kotlinx.browser.localStorage.getItem(KEY)
            ?.let { kotlinx.serialization.json.Json.decodeFromString(EditDraft.serializer(), it) }
            ?.takeIf { user != null && it.userId == user && it.bookId == bookId }
    } catch (_: Throwable) {
        null
    }

    fun save(draft: EditDraft) {
        val user = user() ?: return
        try {
            kotlinx.browser.localStorage.setItem(KEY,
                kotlinx.serialization.json.Json.encodeToString(EditDraft.serializer(), draft.copy(userId = user)))
        } catch (_: Throwable) {
        }
    }

    fun clear(bookId: String) {
        try {
            if (load(bookId) != null) kotlinx.browser.localStorage.removeItem(KEY)
        } catch (_: Throwable) {
        }
    }
}

/** Debounced draft write, kept across recompositions. */
private class DraftTimer { var job: kotlinx.coroutines.Job? = null }

@Composable
fun ManuscriptSection(
    bookId: String?,
    language: Language?,
    isLoggedIn: Boolean,
    onRequireAuth: () -> Unit,
    editing: TextEditing,
) {
    val s = LocalStrings.current
    val pricing = LocalPricing.current
    // `began` (Непрямые действия) is EN-only — RU treats стал/начал + инфинитив
    // as legitimate inchoative aspect, so the analyser never emits it there; drop
    // it from the legend unless the book is English.
    val groups = remember(s, language) {
        issueGroups(s).filter { it.id != "began" || language == Language.ENG }
    }
    // All groups start checked by default.
    val enabled = remember(bookId) { mutableStateOf(groups.map { it.id }.toSet()) }

    // Smart (deep-analysis) highlights: loaded on demand (paid), shown together
    // with the static ones — the unified legend below toggles both. `staticExpanded`
    // drives the «Статика» accordion at the top of the legend (open by default).
    val staticExpanded = remember(bookId) { mutableStateOf(true) }
    val aiSpans = remember(bookId) { mutableStateOf<List<AiHlSpan>>(emptyList()) }
    val aiStatus = remember(bookId) { mutableStateOf(AiStatus.IDLE) }
    // Which scope's result is on screen: "preview" | "full" | null (none yet).
    // Drives the run bar: offer preview only before any run, offer the full scan
    // until the whole book is done, and nothing once "full" is shown — so a button
    // is never a no-op that re-serves what's already there.
    val loadedScope = remember(bookId) { mutableStateOf<String?>(null) }
    // Async full scan vs sync preview — picks the right progress note while LOADING.
    val aiLoadingFull = remember(bookId) { mutableStateOf(false) }
    // Free AI-highlight previews left (from /me); null = not fetched yet.
    val freeLeft = remember(bookId) { mutableStateOf<Int?>(null) }
    // Start time (epoch ms) of a running full scan, null when none. It runs as a
    // batch (minutes to an hour), so it doesn't block the reader: the shown
    // result stays and the run bar notes when the new one is due.
    val batchStartedAt = remember(bookId) { mutableStateOf<Double?>(null) }
    // A full scan failed while an earlier result stays on screen (aiStatus
    // remains LOADED, so its marks don't vanish) — the run bar notes the error.
    val fullFailed = remember(bookId) { mutableStateOf(false) }
    // Read the cache once when AI mode is first opened for this book.
    val aiChecked = remember(bookId) { mutableStateOf(false) }
    // Violation-type catalogue (sections → types) for the tree legend, and the
    // set of expanded section ids.
    val catalog = remember(bookId) { mutableStateOf<AiCatalog?>(null) }
    val expanded = remember(bookId) { mutableStateOf(emptySet<String>()) }
    // AI visibility is per-TYPE (75 types). `typesInit` false = "all on" default
    // (before the catalogue arrives and seeds the set).
    val enabledTypes = remember(bookId) { mutableStateOf(emptySet<String>()) }
    val typesInit = remember(bookId) { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    // Full manuscript text: fetched once per book. `content` null = still
    // loading; `failed` set when the fetch returns nothing.
    val content = remember(bookId) { mutableStateOf<String?>(null) }
    val spans = remember(bookId) { mutableStateOf<List<HlSpan>>(emptyList()) }
    val failed = remember(bookId) { mutableStateOf(false) }
    // Revision of the text on screen (server SHA-256) — a save starts from it.
    val textSha = remember(bookId) { mutableStateOf<String?>(null) }
    LaunchedEffect(bookId) {
        content.value = null
        spans.value = emptyList()
        failed.value = false
        val id = bookId
        if (id == null) { failed.value = true; return@LaunchedEffect }
        val bt = BookRepository.getBookTextWithSha(id)
        if (bt == null || bt.text.isBlank()) { failed.value = true; return@LaunchedEffect }
        // Highlights are best-effort: set them first so the editor mounts once
        // with marks in place; on failure the reader still shows plain text.
        spans.value = BookRepository.getHighlights(id) ?: emptyList()
        textSha.value = bt.sha
        content.value = bt.text
    }

    // ── Editing the text ──
    // Whoever may edit gets the text editable as it stands — no separate
    // mode. While it holds unsaved edits nothing may remount the editor (that
    // would drop them): the mount key stays frozen, new results wait.
    val editedCount = remember(bookId) { mutableStateOf(0) }
    val saving = remember(bookId) { mutableStateOf(false) }
    // One line under the tools: a save / re-check result or why it failed.
    val editNote = remember(bookId) { mutableStateOf<String?>(null) }
    val cancelArmed = remember(bookId) { mutableStateOf(false) }
    // A stored draft for this book, offered for restore on open.
    val draftOffer = remember(bookId) { mutableStateOf<EditDraft?>(null) }
    // Paragraphs of a restored draft — the editor shows them instead of the text.
    val draftParas = remember(bookId) { mutableStateOf<List<String>?>(null) }
    val draftTimer = remember(bookId) { DraftTimer() }
    // Bumped to throw unsaved edits away: remounts the editor from the saved text.
    val discardTick = remember(bookId) { mutableStateOf(0) }
    // Saved, but the new text couldn't be read back: the screen holds the old
    // revision, so the locked editor stays as it is until the page reloads.
    val reloadOwed = remember(bookId) { mutableStateOf(false) }
    // Text ranges changed since the smart scan (marks there not re-checked).
    val unchecked = remember(bookId) { mutableStateOf<List<List<Int>>>(emptyList()) }
    val coarsePointer = remember { window.matchMedia("(pointer: coarse)").matches }
    // Phones: the text opens full screen once it gets the caret.
    val fullView = remember(bookId) { mutableStateOf(false) }
    // Where the page was before that — the browser loses it meanwhile.
    val pageScroll = remember(bookId) { ScrollMemory() }
    // The «what editing gives» card, for those who may not edit yet.
    val lockCard = remember(bookId) { mutableStateOf(false) }
    val lockCardEl = remember(bookId) { mutableStateOf<HTMLElement?>(null) }
    // Re-check of edits: its price (from the server) and whether it runs.
    val recheckPrice = remember(bookId) { mutableStateOf<RecheckEstimate?>(null) }
    val rechecking = remember(bookId) { mutableStateOf(false) }
    val mountKey = remember(bookId) { KeyHolder() }
    val hiddenMarks = remember(bookId) { HiddenMarks() }

    fun toggle(id: String) {
        enabled.value = enabled.value.toMutableSet().apply { if (!add(id)) remove(id) }
    }
    fun toggleExpand(id: String) {
        expanded.value = expanded.value.toMutableSet().apply { if (!add(id)) remove(id) }
    }
    // Per-type AI visibility. Default (before the catalogue seeds the set) is
    // "all on"; the first real toggle materialises the full set, then flips one.
    fun allTypeIds(): Set<String> =
        catalog.value?.sections?.flatMap { it.types }?.map { it.id }?.toSet() ?: emptySet()
    fun ensureTypesInit() {
        if (!typesInit.value) { typesInit.value = true; enabledTypes.value = allTypeIds() }
    }
    // Smart toggles read OFF until a scan has produced results (aiStatus LOADED);
    // then they default to all-on (or whatever the user has since toggled).
    fun typeOn(id: String): Boolean =
        aiStatus.value == AiStatus.LOADED && (!typesInit.value || id in enabledTypes.value)
    fun toggleType(id: String) {
        ensureTypesInit()
        enabledTypes.value = enabledTypes.value.toMutableSet().apply { if (!add(id)) remove(id) }
    }
    fun toggleSection(sec: AiCatSection) {
        ensureTypesInit()
        val ids = sec.types.map { it.id }
        val allOn = ids.all { it in enabledTypes.value }
        enabledTypes.value = enabledTypes.value.toMutableSet().apply {
            if (allOn) removeAll(ids.toSet()) else addAll(ids)
        }
    }

    // Jump-to-issue navigation, driven by the caret: the mounted editor element
    // and the live editor instance (for the ProseMirror selection position).
    val editorEl = remember(bookId) { mutableStateOf<HTMLElement?>(null) }
    val editorInstance = remember(bookId) { mutableStateOf<Any?>(null) }
    // Reader scroll position, carried across editor remounts: any change of
    // the visible marks (legend toggle, new scan result) remounts the editor,
    // which would otherwise throw the text back to the top.
    val readerScroll = remember(bookId) { ScrollMemory() }
    // Index (among the nav stops) of the mark the last next/prev step selected.
    val lastStop = remember(bookId) { NavMemory() }
    // Legend + right-column elements — the legend's height is synced to the
    // right column (reader + run bar) so the two columns are equal height.
    val legendEl = remember(bookId) { mutableStateOf<HTMLElement?>(null) }
    val rightColEl = remember(bookId) { mutableStateOf<HTMLElement?>(null) }

    fun navigate(dir: Int) {
        val root = editorEl.value ?: return
        val ed = editorInstance.value?.asDynamic() ?: return
        val nodes = root.querySelectorAll(".wl-issue")
        val marks = ArrayList<HTMLElement>()
        for (i in 0 until nodes.length) {
            val el = nodes.item(i) as? HTMLElement ?: continue
            // Marks the legend hides stay in the DOM (hidden by CSS) — skip them.
            if (!hiddenMarks.test(el)) marks.add(el)
        }
        if (marks.isEmpty()) return
        // Next/prev relative to the caret's document position.
        val caret = ed.state.selection.from.unsafeCast<Int>()
        fun pos(mark: HTMLElement): Int = ed.view.posAtDOM(mark, 0).unsafeCast<Int>()
        // A mark split by a partially overlapping one renders as pieces; a piece
        // that continues the same mark right where the previous piece ended is
        // not a stop of its own.
        val pieceEnd = HashMap<String, Int>()
        val stops = marks.filter { el ->
            val key = listOf("data-group", "data-kind", "data-title", "data-label")
                .joinToString("\u0000") { el.getAttribute(it) ?: "" }
            val p = pos(el)
            val continues = pieceEnd[key] == p
            pieceEnd[key] = p + (el.textContent?.length ?: 0)
            !continues
        }
        // Stepping from a selected stop goes by document order, not by position:
        // a mark nested at the outer one's start shares its position, so a
        // position search from the caret would skip it.
        val selTo = ed.state.selection.to.unsafeCast<Int>()
        // Marks with the same range all match the selection, so the last stop's
        // index picks the right one among them.
        fun selected(i: Int) = i in stops.indices && pos(stops[i]) == caret &&
            pos(stops[i]) + (stops[i].textContent?.length ?: 0) == selTo
        val cur = if (selected(lastStop.index)) lastStop.index else stops.indices.firstOrNull { selected(it) } ?: -1
        val target = if (cur >= 0) stops[(cur + dir + stops.size) % stops.size]
                     else if (dir > 0) stops.firstOrNull { pos(it) > caret } ?: stops.first()
                     else stops.lastOrNull { pos(it) < caret } ?: stops.last()
        lastStop.index = stops.indexOf(target)
        // Select the mark's text: the styled ::selection is the "you are here"
        // marker. A DOM class would be reverted by ProseMirror's mutation
        // observer (the editor is editable now), whereas the selection is PM's
        // own — and it also parks the caret here so the next step continues on.
        val range = js("({})")
        range.from = pos(target)
        range.to = pos(target) + (target.textContent?.length ?: 0)
        // Focus + select in one chain so the selection actually renders (a
        // blurred editor doesn't paint its selection); we centre the mark
        // ourselves, so tell focus not to scroll.
        ed.chain().focus(null, js("({ scrollIntoView: false })")).setTextSelection(range).run()
        target.asDynamic().scrollIntoView(js("({ block: 'center', behavior: 'smooth' })"))
    }

    // Smart items in the legend stay disabled until a scan has produced results.
    val aiReady = aiStatus.value == AiStatus.LOADED
    // Static + smart highlights coexist. Their group ids overlap (smart sections
    // reuse the static group names), so count and colour them separately.
    val staticCounts = spans.value.groupingBy { it.group }.eachCount()
    // Counted per finding: one cut into pieces by a partial overlap is still one.
    val aiFindings = aiSpans.value.distinctBy<AiHlSpan, Any> { if (it.finding >= 0) it.finding else it }
    val smartCounts = aiFindings.groupingBy { it.group }.eachCount()
    val typeCounts = aiFindings.groupingBy { it.type }.eachCount()
    // Labels from the catalogue (single source), so a span's hover header matches
    // its legend leaf even for older cached results.
    val typeLabels = catalog.value?.sections?.flatMap { it.types }?.associate { it.id to it.label } ?: emptyMap()
    val sectionLabels = catalog.value?.sections?.associate { it.id to it.label } ?: emptyMap()
    // Marks: every static and smart span. The legend hides groups/types with
    // CSS (by data-kind + data-group / data-type, so static and smart stay
    // independent) instead of rebuilding the text — a toggle must not drop
    // unsaved edits. Each mark explains itself on hover — static via the
    // group hint, smart via the per-span note.
    val staticMarks = spans.value.mapNotNull { sp ->
        val g = groups.firstOrNull { it.id == sp.group } ?: return@mapNotNull null
        Mark(sp.start, sp.end, sp.group, g.hint, title = g.label)
    }
    // Smart marks only for a loaded result: during a re-run, or after one that
    // failed, the smart legend is off and couldn't hide them.
    val aiMarks = if (!aiReady) emptyList() else aiSpans.value.map { sp ->
        Mark(sp.start, sp.end, sp.group, sp.feature,
             // Never leave title empty — an unknown catalogue type + blank label
             // would otherwise be dropped by buildMarkedHtml (a paid span vanishing).
             title = typeLabels[sp.type] ?: sp.label.ifBlank { sp.type },
             groupName = sectionLabels[sp.group] ?: "", kind = "smart", type = sp.type)
    }
    val marks: List<Mark> = staticMarks + aiMarks
    val hiddenStatic = groups.map { it.id }.filter { it !in enabled.value }.toSet()
    val hiddenTypes = if (typesInit.value) allTypeIds() - enabledTypes.value else emptySet()
    // A type the catalogue doesn't list (an older result) is off once the
    // catalogue is in: the legend has no switch to turn it on.
    val knownTypes = allTypeIds()
    val hideUnknown = typesInit.value && knownTypes.isNotEmpty()
    hiddenMarks.test = { el ->
        when (el.getAttribute("data-kind")) {
            "static" -> el.getAttribute("data-group") in hiddenStatic
            "smart" -> el.getAttribute("data-type").let { it in hiddenTypes || (hideUnknown && it !in knownTypes) }
            else -> false
        }
    }
    val unknownCss = if (!hideUnknown) emptyList() else listOf(".wl-manuscript-editor .wl-issue[data-kind=\"smart\"]" +
        knownTypes.joinToString("") { ":not([data-type=\"${it.replace("\"", "")}\"])" })
    val hiddenCss = (hiddenStatic.map { ".wl-manuscript-editor .wl-issue[data-kind=\"static\"][data-group=\"$it\"]" } +
        hiddenTypes.map { ".wl-manuscript-editor .wl-issue[data-kind=\"smart\"][data-type=\"${it.replace("\"", "")}\"]" } +
        unknownCss)
        .joinToString(",\n")
        .let { if (it.isEmpty()) "" else "$it { background: none; text-decoration-line: none; cursor: text; }" }
    val hiddenStyle = remember { document.createElement("style") as HTMLElement }
    DisposableEffect(Unit) {
        document.head?.appendChild(hiddenStyle)
        onDispose { hiddenStyle.remove() }
    }
    SideEffect { if (hiddenStyle.textContent != hiddenCss) hiddenStyle.textContent = hiddenCss }
    fun applyOutcome(o: AiHlOutcome, loadedFrom: String) {
        when (o) {
            is AiHlOutcome.Loaded -> {
                aiSpans.value = o.res.spans; loadedScope.value = loadedFrom; aiStatus.value = AiStatus.LOADED
                unchecked.value = o.res.meta?.unchecked ?: emptyList()
                editNote.value = null       // a note about the previous state no longer fits
            }
            AiHlOutcome.NoCache -> aiStatus.value = AiStatus.IDLE
            AiHlOutcome.Failed -> aiStatus.value = AiStatus.ERROR
        }
    }
    fun refreshFree() {
        scope.launch { freeLeft.value = BookRepository.fetchUserInfo()?.freeAiHighlightPreviewsRemaining }
    }
    // Preview (sync, first 30k): free while the quota lasts, else 1 credit.
    // compute=true, force=false → serve an existing result for free; only an
    // absent cache triggers the paid run, so a repeat click never re-charges.
    fun runPreview(force: Boolean = false) {
        val id = bookId ?: return
        aiStatus.value = AiStatus.LOADING; aiLoadingFull.value = false; fullFailed.value = false
        scope.launch {
            applyOutcome(BookRepository.getAiHighlights(id, scope = "preview", compute = true, force = force), "preview")
            refreshFree()
        }
    }
    // Full manuscript (async batch, always paid): serve an existing result for
    // free, otherwise start the paid job and poll to completion. The cache check
    // keeps a repeat click (Alt+Shift re-run bar) from re-charging. The batch takes
    // minutes to an hour, so the shown result stays until the new one arrives.
    // A failed full scan keeps a result already on screen; with none, ERROR.
    // First, though, the cache: when the server lost track of the job (it
    // restarted mid-batch) the analyzer still finishes and writes the result.
    suspend fun onFullFailed(id: String) {
        val o = BookRepository.getAiHighlights(id, scope = "full", compute = false, force = false)
        if (o is AiHlOutcome.Loaded) applyOutcome(o, "full")
        else if (aiStatus.value == AiStatus.LOADED) fullFailed.value = true
        else aiStatus.value = AiStatus.ERROR
    }
    fun runFull(force: Boolean = false) {
        val id = bookId ?: return
        batchStartedAt.value = kotlin.js.Date.now(); fullFailed.value = false
        scope.launch {
            try {
                // force → skip the free cache-serve and re-run (the backend drops
                // the stale cache); otherwise serve an existing result for free.
                val cached = if (force) null
                             else BookRepository.getAiHighlights(id, scope = "full", compute = false, force = false)
                val res = if (cached is AiHlOutcome.Loaded) cached.res
                          else BookRepository.runAiHighlightsFull(id, force)
                applyOutcome(AiHlOutcome.Loaded(res), "full")
            } catch (_: Exception) {
                onFullFailed(id)
            }
            batchStartedAt.value = null
            refreshFree()
        }
    }
    // Serve the cached result (free): prefer the full scan, else the preview.
    // False when neither is cached.
    suspend fun loadCached(id: String): Boolean {
        var o = BookRepository.getAiHighlights(id, scope = "full", compute = false, force = false)
        var sc = "full"
        if (o !is AiHlOutcome.Loaded) {
            o = BookRepository.getAiHighlights(id, scope = "preview", compute = false, force = false); sc = "preview"
        }
        if (o is AiHlOutcome.Loaded) applyOutcome(o, sc)
        return o is AiHlOutcome.Loaded
    }
    // ── Editing: actions ──
    val dirty = editedCount.value > 0 || draftParas.value != null
    fun currentParagraphs(): List<String>? {
        val ed = editorInstance.value?.asDynamic() ?: return null
        return docParagraphs(ed.state.doc).unsafeCast<Array<String>>().toList()
    }
    fun scheduleDraft() {
        val id = bookId ?: return
        val sha = textSha.value ?: return
        draftTimer.job?.cancel()
        draftTimer.job = scope.launch {
            delay(2000)
            currentParagraphs()?.let { EditDrafts.save(EditDraft(id, sha, it)) }
        }
    }
    fun onEdited(n: Int) {
        if (editedCount.value == 0 && n > 0) editing.onStarted()
        if (editedCount.value != n) editedCount.value = n
        cancelArmed.value = false
        if (n > 0) editNote.value = null
        scheduleDraft()
    }
    fun showLockCard(source: String) {
        if (!lockCard.value) editing.onLockedShown(source)
        lockCard.value = true
        // The card sits above the text, so typing further down leaves it off
        // screen — on a phone also under the keyboard, which is no use here.
        if (source == "typing" && coarsePointer) editorInstance.value?.asDynamic()?.commands?.blur()
        scope.launch {
            delay(300)      // card laid out, keyboard gone
            lockCardEl.value?.asDynamic()?.scrollIntoView(js("({ block: 'nearest', behavior: 'smooth' })"))
        }
    }
    fun restoreDraft() {
        val d = draftOffer.value ?: return
        PageAnchoring.hold(); PageAnchoring.release()
        // A pending write of the editor being replaced would land over the draft.
        draftTimer.job?.cancel()
        draftOffer.value = null
        // A new key while dirty: the frozen-key rule below keeps it, so the
        // editor remounts once with the draft.
        mountKey.key = "draft-${kotlin.js.Date.now()}"
        draftParas.value = d.paragraphs
        editedCount.value = changedParagraphs(content.value ?: "", d.paragraphs).size
    }
    fun discardDraft() {
        bookId?.let { EditDrafts.clear(it) }
        draftOffer.value = null
    }
    fun clearEdits() {
        draftTimer.job?.cancel()
        editedCount.value = 0
        draftParas.value = null
        cancelArmed.value = false
    }
    fun cancelEdits() {
        if (!cancelArmed.value) { cancelArmed.value = true; return }
        PageAnchoring.hold(); PageAnchoring.release()
        bookId?.let { EditDrafts.clear(it) }
        clearEdits()
        discardTick.value += 1      // remount from the saved text
        fullView.value = false
    }
    // Everything the saved text changed — text, static marks, the moved smart
    // marks — swapped in at once: one remount, no frame of new text under old
    // offsets. False when it can't all be read: smart marks of the old text
    // left on the new one would sit on the wrong words.
    suspend fun reloadAfterEdit(id: String): Boolean {
        val bt = BookRepository.getBookTextWithSha(id) ?: return false
        val st = BookRepository.getHighlights(id) ?: emptyList()
        var ai: AiHlOutcome = AiHlOutcome.NoCache
        var sc = "full"
        if (isLoggedIn && aiStatus.value == AiStatus.LOADED) {
            ai = BookRepository.getAiHighlights(id, scope = "full", compute = false, force = false)
            if (ai !is AiHlOutcome.Loaded) {
                ai = BookRepository.getAiHighlights(id, scope = "preview", compute = false, force = false)
                sc = "preview"
            }
            if (ai !is AiHlOutcome.Loaded) return false
        }
        spans.value = st
        if (ai is AiHlOutcome.Loaded) applyOutcome(ai, sc)
        textSha.value = bt.sha
        content.value = bt.text
        return true
    }
    /** Apply a save outcome; true when the text was saved. */
    suspend fun finishEdit(id: String, r: TextEditOutcome, changed: Int): Boolean {
        when (r) {
            is TextEditOutcome.Saved -> {
                EditDrafts.clear(id)
                editing.onSaved(r, changed)
                // Read it back; a network blip gets two more tries.
                var reloaded = false
                for (pause in longArrayOf(0, 1000, 3000)) {
                    delay(pause)
                    reloaded = try {
                        reloadAfterEdit(id)
                    } catch (e: kotlinx.coroutines.CancellationException) {
                        throw e
                    } catch (_: Throwable) {        // Ktor/JS reports a failed fetch as an Error
                        false
                    }
                    if (reloaded) break
                }
                clearEdits()
                fullView.value = false
                if (reloaded) {
                    discardTick.value += 1      // a fresh editor, even when the text came back the same
                    editNote.value = s.msSaved(r.marksDropped)
                } else {
                    // Saved, but the screen still holds the old revision: edits to
                    // it would only hit a conflict, so it stays locked until a reload.
                    reloadOwed.value = true
                    editNote.value = s.msSavedReloadPage
                }
                return true
            }
            is TextEditOutcome.Conflict -> {
                editNote.value = when (r.reason) {
                    "scan_running" -> s.msSaveScanRunning
                    "book_busy" -> s.msSaveBusy
                    else -> s.msSaveTextChanged
                }
                editing.onSaveFailed(r.reason)
            }
            is TextEditOutcome.TooShort -> {
                editNote.value = s.msSaveTooShort(r.words)
                editing.onSaveFailed("too_short")
            }
            TextEditOutcome.NotAllowed -> {
                editNote.value = s.msEditLockedHint
                editing.onSaveFailed("not_allowed")
            }
            TextEditOutcome.Failed -> {
                editNote.value = s.msSaveFailed
                editing.onSaveFailed("failed")
            }
        }
        return false
    }
    fun save() {
        val id = bookId ?: return
        if (saving.value) return
        val paras = currentParagraphs() ?: return
        val sha = textSha.value ?: run { editNote.value = s.msSaveFailed; return }
        // A pending draft write would land after the save, on the old revision.
        draftTimer.job?.cancel()
        PageAnchoring.hold()
        saving.value = true; editNote.value = null
        val changed = editedCount.value
        // Typing on while the save runs would be lost on the reload — freeze the text.
        val ed = editorInstance.value?.asDynamic()
        ed?.setEditable(false)
        scope.launch {
            try {
                // Keep the draft until the server has the text.
                EditDrafts.save(EditDraft(id, sha, paras))
                // Saved → a fresh editor takes over (or, if the text couldn't be
                // read back, this one stays locked); not saved → it goes on, editable.
                if (!finishEdit(id, BookRepository.saveBookText(id, sha, paras), changed = changed)) {
                    ed?.setEditable(true)
                }
            } finally {
                saving.value = false
                PageAnchoring.release()
            }
        }
    }
    fun downloadText() {
        val id = bookId ?: return
        val base = editing.fileName?.substringBeforeLast('.')?.ifBlank { null } ?: "book"
        scope.launch {
            BookRepository.downloadBookTxt(id, "$base.txt")
            editing.onDownloaded()
        }
    }
    // «↻ Проверить правки»: smart scan of the changed places only, at the
    // price shown on the button (the server re-checks it before charging).
    fun runRecheck() {
        val id = bookId ?: return
        val price = recheckPrice.value ?: return
        if (rechecking.value || dirty || saving.value) return
        PageAnchoring.hold()
        rechecking.value = true; editNote.value = null
        scope.launch {
            try {
                when (val r = BookRepository.runRecheck(id, price.cost, price.freePreview)) {
                    is RecheckOutcome.Done -> {
                        applyOutcome(AiHlOutcome.Loaded(r.res), price.scope)
                        val rc = r.res.meta?.recheck
                        editNote.value = s.msRechecked(rc?.removed ?: 0, rc?.added ?: 0)
                        editing.onRechecked(price.cost, rc?.removed ?: 0, rc?.added ?: 0)
                    }
                    is RecheckOutcome.PriceChanged -> {
                        recheckPrice.value = r.price
                        editNote.value = s.msRecheckPriceChanged
                    }
                    RecheckOutcome.NoCredits -> {
                        editNote.value = s.aiNoCredits
                        editing.onRecheckFailed("no_credits")
                        editing.onChoosePack()
                    }
                    is RecheckOutcome.Failed -> {
                        editNote.value = s.msRecheckFailed
                        editing.onRecheckFailed(r.reason)
                    }
                }
            } finally {
                rechecking.value = false
                PageAnchoring.release()
            }
            refreshFree()
        }
    }
    // The button is never dead: a click either runs the re-check or says
    // what's missing (the button itself hints at it too).
    fun onRecheckClick() {
        when {
            !editing.canEdit -> showLockCard("recheck")
            rechecking.value -> editNote.value = s.msRecheckRunning
            aiStatus.value != AiStatus.LOADED -> editNote.value = s.msRecheckNeedsScanNote
            unchecked.value.isEmpty() -> editNote.value = s.msRecheckNoChangesNote
            recheckPrice.value == null -> {
                editNote.value = s.msRecheckPriceLoading
                val id = bookId ?: return
                scope.launch { recheckPrice.value = BookRepository.getRecheckEstimate(id) }
            }
            else -> runRecheck()
        }
    }
    // Unsaved edits: leaving the page asks first (the draft is a backup).
    DisposableEffect(dirty) {
        if (!dirty) return@DisposableEffect onDispose {}
        val onBeforeUnload: (Event) -> Unit = { e -> e.preventDefault(); e.asDynamic().returnValue = "" }
        window.addEventListener("beforeunload", onBeforeUnload)
        onDispose { window.removeEventListener("beforeunload", onBeforeUnload) }
    }
    // Phones edit full screen: the page behind must not scroll, and the editor
    // keeps to what the keyboard leaves visible — else the browser shifts the
    // view down to the caret and the tools above go off screen.
    DisposableEffect(fullView.value) {
        if (!fullView.value) return@DisposableEffect onDispose {}
        val body = document.body
        val prev = body?.style?.getPropertyValue("overflow") ?: ""
        body?.style?.setProperty("overflow", "hidden")
        val vv = window.asDynamic().visualViewport
        // Found now: by the time a save or cancel ends full screen, the editor
        // it was found through is already being replaced.
        val column = editorEl.value?.closest(".wl-ms-textcol") as? HTMLElement
        val fit: (Event?) -> Unit = {
            if (vv != null && column != null) {
                column.style.top = "${vv.offsetTop}px"
                column.style.height = "${vv.height}px"
                keepCaretVisible(editorInstance.value, editorEl.value)
            }
        }
        vv?.addEventListener("resize", fit)
        vv?.addEventListener("scroll", fit)
        fit(null)
        onDispose {
            body?.style?.setProperty("overflow", prev)
            vv?.removeEventListener("resize", fit)
            vv?.removeEventListener("scroll", fit)
            column?.style?.apply { removeProperty("top"); removeProperty("height") }
            val y = pageScroll.top
            window.requestAnimationFrame { window.scrollTo(0.0, y) }
        }
    }
    // Unsaved edits left from a closed tab, or from a save turned down because
    // another tab saved first → offer them. A draft made on an older text
    // replaces the current one if restored — the offer says so.
    LaunchedEffect(bookId, textSha.value, editing.canEdit) {
        val id = bookId ?: return@LaunchedEffect
        draftOffer.value = if (editing.canEdit && textSha.value != null) EditDrafts.load(id) else null
    }
    // The price of re-checking, whenever the changed places change.
    LaunchedEffect(bookId, textSha.value, unchecked.value, aiStatus.value, editing.canEdit) {
        recheckPrice.value = null
        val id = bookId ?: return@LaunchedEffect
        if (isLoggedIn && editing.canEdit && aiStatus.value == AiStatus.LOADED && unchecked.value.isNotEmpty()) {
            recheckPrice.value = BookRepository.getRecheckEstimate(id)
        }
    }

    // On first opening AI mode, read the cache (free) and show it if present;
    // otherwise fall back to IDLE so the user can trigger the paid run.
    LaunchedEffect(bookId) {
        val id = bookId ?: return@LaunchedEffect
        if (catalog.value == null) catalog.value = BookRepository.getAiCatalog()
        // Anonymous users can't own a cached scan and can't hit the owner-only
        // AI endpoints (they'd 401), so skip those probes and show the same run
        // bar an authenticated user sees before their first scan — freeLeft is
        // seeded from pricing so the preview button reads «free (N)» too. Any
        // run-button click routes to sign-in (see the button handlers below).
        if (!isLoggedIn) {
            freeLeft.value = pricing.freeAiHighlightPreviewsPerUser
            aiChecked.value = true
            aiStatus.value = AiStatus.IDLE
            return@LaunchedEffect
        }
        if (freeLeft.value == null) freeLeft.value = BookRepository.fetchUserInfo()?.freeAiHighlightPreviewsRemaining
        if (!aiChecked.value) {
            aiChecked.value = true
            aiStatus.value = AiStatus.LOADING; aiLoadingFull.value = false
            suspend fun awaitFull(jid: String) {
                try {
                    applyOutcome(AiHlOutcome.Loaded(BookRepository.awaitAiHighlightsFull(jid)), "full")
                } catch (_: Exception) { onFullFailed(id) }
            }
            // Auto-serve a cached result (free): prefer the full scan, else preview.
            // A full scan still running from another tab/session is re-attached:
            // a batch one always (the cached result stays on screen meanwhile), a
            // regular one only when nothing is cached.
            val job = BookRepository.getActivePromoJobs(id).firstOrNull { it.kind == "AI_HIGHLIGHTS_FULL" }
            val jid = job?.jobId
            if (jid != null && job?.mode == "recheck") {
                // A re-check of edits runs on top of a result already on disk.
                if (!loadCached(id)) aiStatus.value = AiStatus.IDLE
                rechecking.value = true
                when (val r = BookRepository.awaitRecheck(id, jid)) {
                    is RecheckOutcome.Done -> {
                        applyOutcome(AiHlOutcome.Loaded(r.res), loadedScope.value ?: "full")
                        val rc = r.res.meta?.recheck
                        editNote.value = s.msRechecked(rc?.removed ?: 0, rc?.added ?: 0)
                    }
                    is RecheckOutcome.Failed -> {
                        editNote.value = s.msRecheckFailed
                        editing.onRecheckFailed(r.reason)      // the server refunded it: fresh counters
                    }
                    else -> {}
                }
                rechecking.value = false
            } else if (jid != null && job?.mode == "batch") {
                batchStartedAt.value = job?.startedAt ?: kotlin.js.Date.now()
                if (!loadCached(id)) aiStatus.value = AiStatus.IDLE
                awaitFull(jid)
                batchStartedAt.value = null
            } else if (!loadCached(id)) {
                if (jid != null) {
                    aiLoadingFull.value = true
                    awaitFull(jid)
                } else aiStatus.value = AiStatus.IDLE
            }
        }
    }
    // Seed per-type visibility to "all on" once the catalogue arrives.
    LaunchedEffect(catalog.value) {
        if (catalog.value != null && !typesInit.value) { typesInit.value = true; enabledTypes.value = allTypeIds() }
    }
    // Match the legend height to the right column (reader + run bar) so the two
    // columns are equal height; re-measured (next frame) when the run bar changes
    // — status text, buttons appearing/disappearing.
    LaunchedEffect(aiStatus.value, aiLoadingFull.value, batchStartedAt.value, fullFailed.value, freeLeft.value, content.value) {
        window.requestAnimationFrame {
            val rc = rightColEl.value
            val lg = legendEl.value
            if (rc != null && lg != null) lg.style.height = "${rc.offsetHeight}px"
        }
    }

    // Two-column reader grid: legend (left, stretched to full height) + the
    // text-and-controls column (right).
    Div({ classes("wl-block-grid", "wl-ms-top") }) {
    // Legend — a sidebar-style card in the left column. Each swatch reuses the
    // highlight CSS, so the sample = the real appearance in the text.
    Div({
        ref { el ->
            legendEl.value = el
            val detachTip = attachHintTooltip(el, "[data-label]")
            onDispose { detachTip(); legendEl.value = null }
        }
        classes("wl-ms-legend")
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("box-sizing", "border-box")
            property("align-self", "start")
        }
    }) {
        // Scrollable list: «Статика» accordion (open by default) + smart sections.
        Div({ classes("wl-ms-legend-scroll") }) {
            val staticAllOn = groups.isNotEmpty() && groups.all { it.id in enabled.value }
            Div({ classes("wl-ai-tree") }) {
                Div({ classes("wl-ai-sec", "wl-ai-sec-static"); if (staticExpanded.value) classes("open") }) {
                    Div({ classes("wl-ai-sec-head"); onClick { staticExpanded.value = !staticExpanded.value } }) {
                        Div({
                            classes("wl-ai-toggle"); if (staticAllOn) classes("on")
                            onClick {
                                it.stopPropagation()
                                enabled.value = if (staticAllOn) emptySet() else groups.map { g -> g.id }.toSet()
                            }
                        })
                        Span({ classes("wl-ai-sec-label"); style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurface) } }) { Text(s.hlModeStatic) }
                        Span({ classes("wl-legend-count") }) { Text(spans.value.size.toString()) }
                        Span({ classes("wl-ai-caret") }) { Text("▸") }
                    }
                    Div({ classes("wl-ai-types") }) {
                        LegendRows(groups, enabled.value, staticCounts) { toggle(it) }
                    }
                }
            }
            AiLegendTree(
                catalog.value?.sections ?: emptyList(),
                smartCounts, typeCounts, { typeOn(it) }, expanded.value,
                { toggleType(it) }, { toggleSection(it) }, { toggleExpand(it) },
                enabled = aiReady,
            )
        }
        // Footer (pinned at the bottom of the legend): issue navigation.
        Div({ classes("wl-ms-legend-foot") }) {
            Div({ classes("wl-nav-row") }) {
                Span({ classes("wl-nav-btn"); onMouseDown { it.preventDefault() }; onClick { navigate(-1) } }) { Text("↑ ${s.hlPrevIssue}") }
                Span({ classes("wl-nav-btn"); onMouseDown { it.preventDefault() }; onClick { navigate(1) } }) { Text("↓ ${s.hlNextIssue}") }
            }
        }
    }

    // Text column: the read-only editor once loaded, else a loading / error
    // placeholder sized like it so the layout doesn't jump.
    val text = content.value
    when {
        text != null -> {
            // Right column: the reader + the smart run bar under it.
            Div({
                ref { el ->
                    rightColEl.value = el
                    val onResize: (Event) -> Unit = { legendEl.value?.let { it.style.height = "${el.offsetHeight}px" } }
                    window.addEventListener("resize", onResize)
                    onDispose { window.removeEventListener("resize", onResize); rightColEl.value = null }
                }
                style { property("display", "flex"); property("flex-direction", "column"); property("min-width", "0") }
            }) {
            Div({ classes("wl-ms-textcol"); if (fullView.value) classes("wl-ms-fullscreen") }) {
            // Tools above the text. Those who may not edit yet see them locked;
            // pressing one — or typing in the text — opens the card below. With
            // unsaved edits the row turns into save / cancel.
            Div({ classes("wl-ms-edit-bar") }) {
                if (dirty || saving.value) {
                    // A save in progress shows on the label; a click then does nothing new.
                    Span({
                        classes("wl-ms-edit-btn", "primary")
                        onClick { if (!saving.value) save() }
                    }) { Text(if (saving.value) s.msSaving else s.msSave) }
                    Span({
                        classes("wl-ms-edit-btn")
                        onClick { if (!saving.value) cancelEdits() }
                    }) { Text(if (cancelArmed.value) s.msCancelConfirm else s.msCancelEdit) }
                    if (editedCount.value > 0) Span({ classes("wl-ms-edit-meta") }) { Text(s.msEditedCount(editedCount.value)) }
                } else {
                    // The label says what the click will do: the price, or what's
                    // missing first (a purchase, a smart scan, some edits). The
                    // button lays its parts out as a flex row, which drops plain
                    // spaces at their edges — hence the no-break spaces.
                    val locked = !editing.canEdit
                    val price = recheckPrice.value
                    val sep = "\u00A0·\u00A0"
                    Span({
                        classes("wl-ms-edit-btn"); if (locked) classes("locked")
                        onClick { onRecheckClick() }
                    }) {
                        Text("\u21BB\u00A0${if (rechecking.value) s.msRechecking else s.msRecheck}")
                        when {
                            locked -> Text("\u00A0\uD83D\uDD12")
                            rechecking.value -> {}
                            aiStatus.value != AiStatus.LOADED ->
                                Span({ classes("wl-ms-edit-hint") }) { Text(sep + s.msRecheckNeedsScan) }
                            unchecked.value.isEmpty() ->
                                Span({ classes("wl-ms-edit-hint") }) { Text(sep + s.msRecheckNoChanges) }
                            price == null -> Text("$sep…")
                            // One of the free smart checks pays — not the same «free» as cached chunks.
                            price.freePreview -> Text(sep + s.msRecheckFreeSlot)
                            price.cost == 0 -> Text(sep + s.msRecheckFree)
                            else -> { Text("$sep${price.cost}\u00A0"); DragonIcon(sizePx = 14, tint = DragonTint.GOLD) }
                        }
                    }
                    if (isLoggedIn) {
                        Span({ classes("wl-ms-edit-link"); onClick { downloadText() } }) { Text("\u2913 ${s.msDownloadTxt}") }
                    }
                }
                if (fullView.value) {
                    Span({
                        classes("wl-ms-edit-link")
                        onClick {
                            fullView.value = false
                            editorInstance.value?.asDynamic()?.commands?.blur()
                        }
                    }) { Text(s.msDone) }
                }
            }
            // When the text in the reader was last edited (it no longer matches the file).
            val editedAt = editing.editedAt
            if (editedAt != null && !dirty && !saving.value) {
                Div({ classes("wl-ms-edit-bar") }) {
                    Span({ classes("wl-ms-edit-meta") }) { Text(s.msEditedAt(formatEditDate(editedAt))) }
                }
            }
            editNote.value?.let { note -> Div({ classes("wl-ms-ai-note", "wl-ms-edit-note") }) { Text(note) } }
            if (draftOffer.value != null && !dirty) {
                Div({ classes("wl-ms-edit-bar") }) {
                    val stale = draftOffer.value?.baseSha != textSha.value
                    Span({ classes("wl-ms-edit-meta") }) { Text(if (stale) s.msDraftFoundStale else s.msDraftFound) }
                    Span({ classes("wl-ms-edit-link"); onClick { restoreDraft() } }) { Text(s.msDraftRestore) }
                    Span({ classes("wl-ms-edit-link"); onClick { discardDraft() } }) { Text(s.msDraftDiscard) }
                }
            }
            if (lockCard.value && !editing.canEdit) {
                LockCard(
                    isLoggedIn = isLoggedIn,
                    price = startPackPrice(pricing),
                    onChoose = { editing.onChoosePack() },
                    onClose = { lockCard.value = false },
                    onElement = { lockCardEl.value = it },
                )
            }
            // Remount when the text or the marks change (a save, smart results,
            // a re-check). Never while the text holds unsaved edits — that would
            // drop them: the key stays as it was until they're saved or dropped.
            val liveKey = "${editing.canEdit}-${textSha.value}-${text.length}-${aiSpans.value.hashCode()}-" +
                "${unchecked.value.hashCode()}-${discardTick.value}-$aiReady"
            if (!dirty && !saving.value && !reloadOwed.value) mountKey.key = liveKey
            key(bookId, mountKey.key) {
                Div({
                    classes("wl-manuscript-editor")
                    if (editing.canEdit) classes("wl-ms-editable")
                    style { editorPageStyle() }
                    ref { element ->
                        editorEl.value = element
                        val draft = draftParas.value
                        val html = draft?.let { paragraphsHtml(it) } ?: buildMarkedHtml(text, marks)
                        val uncheckedParas = if (draft != null) emptyList() else uncheckedParagraphs(text, unchecked.value)
                        val editedParas = draft?.let { changedParagraphs(text, it) } ?: emptyList()
                        val editor = runCatching {
                            createManuscriptEditor(
                                element, html, editing.canEdit, uncheckedParas, editedParas,
                                onEdited = { n -> onEdited(n) },
                                onLocked = { showLockCard("typing") },
                                onFocus = {
                                    if (coarsePointer && !fullView.value) {
                                        pageScroll.top = window.scrollY
                                        fullView.value = true
                                    }
                                },
                            )
                        }.getOrElse { e ->
                            console.error("Manuscript editor failed to mount", e)
                            null
                        }
                        editorInstance.value = editor
                        val detachTip = attachHintTooltip(element, isHidden = { hiddenMarks.test(it) })
                        // Wide (≥1100px, legend beside text): cap the text at the
                        // legend's height so the columns line up (text scrolls).
                        // Below that the legend sits under the text → a fixed cap.
                        // Re-measured on resize.
                        // Fixed reader height; the legend column caps at the same
                        // 580px (CSS) so the two columns line up on wide screens.
                        element.style.maxHeight = "580px"
                        // Back to the previous editor's position — next frame, once
                        // the content is laid out. The old editor may be disposed
                        // after this one mounts, so the position is kept on scroll.
                        val top = readerScroll.top
                        window.requestAnimationFrame { element.scrollTop = top }
                        val onScroll: (Event) -> Unit = { readerScroll.top = element.scrollTop }
                        element.addEventListener("scroll", onScroll)
                        // The reader is a tall inner-scroll box; keep it from
                        // trapping the page scroll. Desktop: the wheel scrolls the
                        // PAGE unless the reader is focused (clicked into) — then it
                        // scrolls the text natively. Touch: one finger scrolls the
                        // page (the reader is overflow:hidden on coarse pointers, so
                        // it isn't user-draggable), two fingers scroll the text.
                        val onWheel: (Event) -> Unit = { e ->
                            val a = document.activeElement
                            if (a == null || !element.contains(a)) {
                                e.preventDefault()
                                window.scrollBy(0.0, e.asDynamic().deltaY.unsafeCast<Double>())
                            }
                        }
                        var lastTouchY = 0.0
                        val onTouchStart: (Event) -> Unit = { e ->
                            val t = e.asDynamic().touches
                            if (t.length.unsafeCast<Int>() >= 2)
                                lastTouchY = (t[0].clientY.unsafeCast<Double>() + t[1].clientY.unsafeCast<Double>()) / 2
                        }
                        val onTouchMove: (Event) -> Unit = { e ->
                            val t = e.asDynamic().touches
                            if (t.length.unsafeCast<Int>() >= 2) {
                                e.preventDefault()
                                val y = (t[0].clientY.unsafeCast<Double>() + t[1].clientY.unsafeCast<Double>()) / 2
                                element.scrollTop = element.scrollTop - (y - lastTouchY)
                                lastTouchY = y
                            }
                        }
                        element.addEventListener("wheel", onWheel, js("({ passive: false })"))
                        element.addEventListener("touchstart", onTouchStart, js("({ passive: true })"))
                        element.addEventListener("touchmove", onTouchMove, js("({ passive: false })"))
                        onDispose {
                            // Leaving with a draft write pending (another book, another page
                            // of the app): write it now, while this editor still has the text.
                            val sha = textSha.value
                            if (draftTimer.job?.isActive == true && editor != null && bookId != null && sha != null) {
                                draftTimer.job?.cancel()
                                val paras = docParagraphs(editor.asDynamic().state.doc).unsafeCast<Array<String>>().toList()
                                EditDrafts.save(EditDraft(bookId, sha, paras))
                            }
                            element.removeEventListener("scroll", onScroll)
                            editor?.destroy(); detachTip()
                            element.removeEventListener("wheel", onWheel)
                            element.removeEventListener("touchstart", onTouchStart)
                            element.removeEventListener("touchmove", onTouchMove)
                            editorEl.value = null; editorInstance.value = null
                        }
                    }
                })
            }
            Div({ classes("wl-ms-scrollhint") }) {
                Span({ classes("wl-ms-hint-mouse") }) { Text(s.hlScrollHintMouse) }
                Span({ classes("wl-ms-hint-touch") }) { Text(s.hlScrollHintTouch) }
            }
            }
            // Smart run bar (status + run controls) under the text.
            Div({ classes("wl-ms-runbar") }) {
                // Alt+Shift re-shows the run buttons for scopes already done, so the
                // SAME button (same label + price) triggers a paid re-run. The normal
                // path never re-charges for a result already on screen.
                val altShift = rememberAltShiftHeld()
                val batchSince = batchStartedAt.value
                val notRunning = aiStatus.value != AiStatus.LOADING && batchSince == null
                val showPreview = loadedScope.value == null || altShift  // first run, or re-run (altShift)
                val showFull = loadedScope.value != "full" || altShift   // first/upgrade, or re-run (altShift)
                val force = altShift  // held → a click forces a fresh paid re-run
                val hasButtons = notRunning && (showPreview || showFull) && !dirty
                when {
                    batchSince != null -> BatchScanNote(batchSince)
                    aiStatus.value == AiStatus.LOADING ->
                        SmartScanProgress(
                            estTotalSeconds = estSmartScanSeconds(content.value?.length ?: 0, aiLoadingFull.value),
                            full = aiLoadingFull.value,
                        )
                    aiStatus.value == AiStatus.ERROR || fullFailed.value ->
                        Div({ classes("wl-ms-ai-note") }) { Text(s.hlAiError) }
                    aiStatus.value == AiStatus.LOADED && aiSpans.value.isEmpty() && unchecked.value.isEmpty() ->
                        Div({ classes("wl-ms-ai-note") }) { Text(s.hlAiEmpty) }
                    else -> {}
                }
                if (rechecking.value) {
                    Div({ classes("wl-ms-ai-note") }) { Text(s.msRecheckRunning) }
                } else if (aiStatus.value == AiStatus.LOADED && unchecked.value.isNotEmpty()) {
                    Div({ classes("wl-ms-ai-note") }) { Text(s.msUncheckedNote) }
                }
                // Feature description — visible whenever a run control is shown.
                if (hasButtons) Div({ classes("wl-ms-ai-note") }) { Text(s.hlAiIntro) }
                if (hasButtons) {
                    val fullCost = calcAiCost(content.value?.length ?: 0, pricing.aiHighlightCharsPerCredit)
                    // Full-scan rate, per 100k chars — derived from the SAME calc +
                    // pricing field as the real charge above, so the two never drift.
                    if (showFull) Div({ classes("wl-ms-ai-note") }) {
                        Text(s.hlAiFullRate(calcAiCost(100_000, pricing.aiHighlightCharsPerCredit).toString()))
                        Text(" "); DragonIcon(sizePx = 14, tint = DragonTint.GOLD); Text(" ")
                        Text(s.hlAiFullRateUnit)
                    }
                    // Anonymous visitors must sign in before a scan runs (the buttons
                    // route to sign-in on click) — say so up front.
                    if (!isLoggedIn) Div({ classes("wl-ms-ai-note") }) { Text(s.hlAiSignInToRun) }
                    Div({ classes("wl-ms-ai-buttons") }) {
                        if (showPreview) {
                            // Paid (no free slot left) → show the cost with the dragon
                            // credit icon instead of the word, as everywhere else.
                            val previewPaid = (freeLeft.value ?: 0) <= 0
                            // Button + its free-quota caption in one cell, so the caption
                            // sits directly under THIS button (not centered under the row).
                            Div({ classes("wl-ms-ai-cell") }) {
                            Div({ style { genButtonStyle() }; onClick { if (isLoggedIn) runPreview(force) else onRequireAuth() } }) {
                                Text(s.hlAiPreview(freeLeft.value))
                                if (previewPaid) { Text(" "); DragonIcon(sizePx = 16, tint = DragonTint.DARK) }
                            }
                            // Only while free previews remain — once they're used up the
                            // button shows the credit price and no caption is needed.
                            if (!previewPaid) FreeQuotaHint(
                                remaining = freeLeft.value ?: 0,
                                total = pricing.freeAiHighlightPreviewsPerUser,
                                hintKind = FreeQuotaKind.HIGHLIGHT,
                            )
                            }
                        }
                        if (showFull) {
                            Div({ classes("wl-ms-ai-cell") }) {
                            Div({ style { genButtonStyle() }; onClick { if (isLoggedIn) runFull(force) else onRequireAuth() } }) {
                                Text(s.hlAiFull(fullCost.toString()))
                                Text(" "); DragonIcon(sizePx = 16, tint = DragonTint.DARK)
                            }
                            }
                        }
                    }
                }
            }
            }
        }
        failed.value -> ManuscriptPlaceholder(s.manuscriptUnavailable, WLColors.OnSurfaceVariant)
        else -> ManuscriptPlaceholder(s.manuscriptLoading, WLColors.OnSurfaceVariant)
    }
    }
}

// Full-width pill CTA — same style as the other generation buttons
// (AiInsightsSection / promo): gold Secondary fill, rounded, centred.
private fun org.jetbrains.compose.web.css.StyleScope.genButtonStyle() {
    property("background-color", WLColors.Secondary)
    fg(WLColors.OnSecondary)
    typography(TextLevel.LabelLarge)
    property("padding", "13px 16px")
    property("border-radius", "120px")
    property("text-align", "center")
    clickableCursor()
    property("box-sizing", "border-box")
    fillWidth()
}

private fun org.jetbrains.compose.web.css.StyleScope.editorPageStyle() {
    bg(WLColors.Surface)
    property("border", "1px solid ${WLColors.OutlineVariant}")
    property("border-radius", "8px")
    property("padding", "18px 22px")
    property("overflow-y", "auto")
    property("box-sizing", "border-box")
    // max-height is set imperatively (syncHeight): legend height on wide, a
    // fixed cap on narrow. Kept out of the Compose style so a recomposition
    // (group toggle) doesn't reset it.
}

@Composable
private fun ManuscriptPlaceholder(message: String, color: String) {
    Div({
        style {
            editorPageStyle()
            property("min-height", "160px")
            property("max-height", "none")
            property("display", "flex")
            property("align-items", "center")
            property("justify-content", "center")
            typography(TextLevel.BodyMedium)
            fg(color)
        }
    }) { Text(message) }
}

/** A running full scan. The batch reports no progress and its wait depends on
 *  the provider's load (ours took 1–4 min; the analyzer cancels it at 30 min
 *  and computes the rest itself), so a plain note with the start time
 *  replaces the progress bar. */
@Composable
private fun BatchScanNote(startedAtMs: Double) {
    val s = LocalStrings.current
    val d = kotlin.js.Date(startedAtMs)
    val at = "${d.getHours()}:${d.getMinutes().toString().padStart(2, '0')}"
    Div({ classes("wl-ms-ai-note") }) { Text(s.hlAiBatchNote(at)) }
}

/** Rough wall-clock estimate for the smart scan, mirroring the analyzer's
 *  chunking: HEAD_CHARS (10k)-sized chunks scanned DEFAULT_CHUNK_WORKERS (5) in
 *  parallel, so one ~5-chunk wave ≈ 30s (calibrated off the preview wave). The
 *  preview is always the first 30k = one wave. Client-side only — shown as "~". */
private fun estSmartScanSeconds(chars: Int, full: Boolean): Int {
    if (!full) return 30
    val chunks = maxOf(1, (chars + 9_999) / 10_000)
    val waves = (chunks + 4) / 5
    return waves * 30
}

/** Progress + ETA for a running smart scan. There are no real stages to report
 *  (the analyzer returns one result), so the bar is an ESTIMATE: elapsed vs the
 *  size-based [estTotalSeconds], capped below 100% so a slow run never shows
 *  "done" early. Rotates the generic loading lines during the long full scan. */
@Composable
private fun SmartScanProgress(estTotalSeconds: Int, full: Boolean) {
    val s = LocalStrings.current
    val elapsed = remember { mutableStateOf(0) }
    LaunchedEffect(Unit) { while (true) { delay(1000); elapsed.value += 1 } }
    val shuffled = remember(s.loadingMessages) { s.loadingMessages.shuffled() }
    val msgIndex = remember { mutableStateOf(0) }
    LaunchedEffect(shuffled) {
        while (true) { delay(5000); if (shuffled.isNotEmpty()) msgIndex.value = (msgIndex.value + 1) % shuffled.size }
    }
    val progress = if (estTotalSeconds > 0) (elapsed.value.toFloat() / estTotalSeconds).coerceIn(0f, 0.97f) else 0f
    val remaining = (estTotalSeconds - elapsed.value).coerceAtLeast(0)
    val note = when {
        full && shuffled.isNotEmpty() -> shuffled[msgIndex.value]
        full -> s.hlAiFullLoading
        else -> s.hlAiLoading
    }
    Div({ classes("wl-ms-ai-note") }) { Text(note) }
    Div({ classes("wl-ai-progress"); style { fillWidth() } }) { MdLinearProgress(value = progress) }
    Div({ style { flexRow(gapPx = 8); fillWidth(); property("justify-content", "space-between") } }) {
        Span({ style { typography(TextLevel.BodySmall); fg(WLColors.OnSurfaceVariant) } }) {
            Text(if (remaining > 0) "~ ${formatTime(remaining)}" else "…")
        }
        Span({ style { typography(TextLevel.LabelSmall); fg(WLColors.OnSurfaceVariant) } }) {
            Text(formatTime(elapsed.value))
        }
    }
}

// ─── Text + spans → paragraph HTML ─────────────────────────────────────────
// Wraps each stylistic-issue span in a <mark data-group data-label> (the
// styleIssue TipTap mark preserves those attributes), then splits the text into
// <p> paragraphs on newlines. Offsets index into `text` verbatim. HTML is
// escaped so stray `<`/`&` can't break (or inject into) it.
// One resolved highlight to render: char offsets, legend group (drives colour +
// hide class), the hover detail body (`data-label`), plus an optional header —
// `title` (the violation) and `groupName` (its group), shown above the body.
private class Mark(val start: Int, val end: Int, val group: String, val label: String,
                   val title: String = "", val groupName: String = "", val kind: String = "static",
                   val type: String = "")

// Overlapping violations nest: the text is cut at every span boundary and each
// run is wrapped in all marks covering it, outermost (earliest, then longest)
// first. The editor merges a mark's adjacent runs back into one; a mark split by
// a partial overlap stays two pieces. Runs are also cut at newlines, so no tag
// crosses a paragraph.
private fun buildMarkedHtml(source: String, sourceMarks: List<Mark>): String {
    // Heading markers are cut out of the shown text; the marks shift with it.
    val hidden = ArrayList<IntArray>()             // [start, end) of each marker in `source`
    val markerOfLine = HashMap<Int, String>()
    var lineStart = 0
    source.split('\n').forEachIndexed { i, line ->
        headingMarker.find(line)?.let { m ->
            hidden.add(intArrayOf(lineStart + m.range.first, lineStart + m.range.last + 1))
            markerOfLine[i] = m.value
        }
        lineStart += line.length + 1
    }
    fun shift(o: Int): Int {
        var d = 0
        for (c in hidden) {
            if (o >= c[1]) d += c[1] - c[0]
            else { if (o > c[0]) d += o - c[0]; break }
        }
        return o - d
    }
    val text = if (hidden.isEmpty()) source else buildString {
        var p = 0
        for (c in hidden) { append(source.substring(p, c[0])); p = c[1] }
        append(source.substring(p))
    }
    val marks = if (hidden.isEmpty()) sourceMarks else sourceMarks.map {
        Mark(shift(it.start), shift(it.end), it.group, it.label, it.title, it.groupName, it.kind, it.type)
    }
    val valid = marks
        .filter { it.start >= 0 && it.start < it.end && it.end <= text.length && (it.label.isNotEmpty() || it.title.isNotEmpty()) }
        .distinctBy { listOf(it.start, it.end, it.group, it.kind, it.title, it.label, it.type) }
        .sortedWith(compareBy<Mark> { it.start }.thenByDescending { it.end })
    val cuts = (listOf(0, text.length) + valid.flatMap { listOf(it.start, it.end) }).distinct().sorted()
    val body = StringBuilder()
    val open = ArrayList<Mark>()   // marks covering the current run, outermost first
    var next = 0
    for (i in 0 until cuts.size - 1) {
        val a = cuts[i]
        open.removeAll { it.end <= a }
        while (next < valid.size && valid[next].start <= a) open.add(valid[next++])
        val run = text.substring(a, cuts[i + 1])
        if (open.isEmpty()) { body.append(escapeHtml(run)); continue }
        run.split('\n').forEachIndexed { j, piece ->
            if (j > 0) body.append('\n')
            if (piece.isEmpty()) return@forEachIndexed
            open.forEach { body.append(markOpenTag(it)) }
            body.append(escapeHtml(piece))
            repeat(open.size) { body.append("</mark>") }
        }
    }

    // A paragraph per non-blank line of the TEXT (not of the HTML: a blank line
    // inside a long mark would otherwise become an empty paragraph) — the same
    // count [uncheckedParagraphs] and a save's paragraph list rely on.
    val out = StringBuilder()
    val textLines = text.split('\n')
    body.toString().split('\n').forEachIndexed { i, line ->
        if (textLines.getOrNull(i)?.trim().isNullOrEmpty()) return@forEachIndexed
        val t = line.trim()
        if (t.isEmpty()) return@forEachIndexed
        val marker = markerOfLine[i]
        out.append(if (marker == null) "<p>" else "<p data-heading=\"${escapeAttr(marker)}\">").append(t).append("</p>")
    }
    return out.toString()
}

// «### » the converter puts before a heading (FB2/EPUB/DOCX titles). The deep
// analysis splits chapters by it, so book.txt keeps it; the reader shows the
// heading without it, and the paragraph carries it as an attribute that a save
// puts back ([docParagraphs]). Only before text: a bare «###» is a separator.
private val headingMarker = Regex("^[ \\t]*###[ \\t]+(?=\\S)")

private fun markOpenTag(m: Mark): String = buildString {
    append("<mark data-group=\"").append(m.group)
        .append("\" data-kind=\"").append(m.kind)
        .append("\" data-label=\"").append(escapeAttr(m.label)).append("\"")
    if (m.title.isNotEmpty()) append(" data-title=\"").append(escapeAttr(m.title)).append("\"")
    if (m.groupName.isNotEmpty()) append(" data-glabel=\"").append(escapeAttr(m.groupName)).append("\"")
    if (m.type.isNotEmpty()) append(" data-type=\"").append(escapeAttr(m.type)).append("\"")
    append(">")
}

private fun escapeHtml(s: String): String =
    s.replace("&", "&amp;").replace("<", "&lt;").replace(">", "&gt;")

private fun escapeAttr(s: String): String =
    // Newlines must go: buildMarkedHtml splits the assembled HTML on '\n', so a
    // newline inside an attribute value would bisect the tag. Collapse to a space.
    s.replace("&", "&amp;").replace("\"", "&quot;").replace("\n", " ").replace("\r", " ")

/** Indices of the reader's paragraphs (non-blank lines of [text], as
 *  [buildMarkedHtml] emits them) that overlap [ranges] — those get the
 *  «not re-checked» bar. */
private fun uncheckedParagraphs(text: String, ranges: List<List<Int>>): List<Int> {
    if (ranges.isEmpty()) return emptyList()
    val out = ArrayList<Int>()
    var pos = 0
    var para = 0
    for (line in text.split('\n')) {
        val start = pos
        val end = pos + line.length
        if (line.trim().isNotEmpty()) {
            if (ranges.any { r -> r.size == 2 && r[0] < end && r[1] > start }) out.add(para)
            para++
        }
        pos = end + 1
    }
    return out
}

/** A restored draft: plain paragraphs, no marks. */
private fun paragraphsHtml(paragraphs: List<String>): String =
    paragraphs.joinToString("") { p ->
        val m = headingMarker.find(p)
        if (m == null) "<p>${escapeHtml(p)}</p>"
        else "<p data-heading=\"${escapeAttr(m.value)}\">${escapeHtml(p.substring(m.range.last + 1))}</p>"
    }

/** Indices of the [draft] paragraphs that [text] doesn't have — what a
 *  restored draft changed. Matched as a multiset: a moved paragraph doesn't
 *  count, a removed one leaves nothing to mark. */
private fun changedParagraphs(text: String, draft: List<String>): List<Int> {
    val space = Regex("\\s+")
    fun norm(s: String) = s.trim().replace(space, " ")
    val left = HashMap<String, Int>()
    for (line in text.split('\n')) {
        val n = norm(line)
        if (n.isNotEmpty()) left[n] = (left[n] ?: 0) + 1
    }
    return draft.indices.filter { i ->
        val n = norm(draft[i])
        val have = left[n] ?: 0
        if (have > 0) left[n] = have - 1
        have == 0
    }
}

/** «1 ноября» / «November 1» for an ISO timestamp; empty when unparseable. */
internal fun formatEditDate(iso: String): String {
    val d = kotlin.js.Date(iso)
    if (d.getTime().isNaN()) return ""
    val locale = if (appLocaleSnapshot() == "en") "en-US" else "ru-RU"
    return d.asDynamic().toLocaleDateString(locale, js("({ day: 'numeric', month: 'long' })")).unsafeCast<String>()
}


/** The cheapest pack's price in the visitor's currency: «444 ₽» / «$9». */
private fun startPackPrice(p: Pricing): String {
    fun fmt(v: Double) = if (v % 1.0 == 0.0) v.toInt().toString() else v.toString()
    return if (appLocaleSnapshot() == "en") "$" + fmt(p.packStartPriceUsd) else fmt(p.packStartPriceRub) + " \u20BD"
}

/** What editing the text gives — for those who may not edit yet. Opened by
 *  a locked tool or by typing in the text; «Выбрать пакет» goes to top-up. */
@Composable
private fun LockCard(
    isLoggedIn: Boolean, price: String, onChoose: () -> Unit, onClose: () -> Unit,
    onElement: (HTMLElement?) -> Unit,
) {
    val s = LocalStrings.current
    Div({
        classes("wl-ms-lock-card")
        ref { el -> onElement(el); onDispose { onElement(null) } }
    }) {
        Div({ classes("wl-ms-lock-title") }) { Text("\u270E ${s.msLockTitle}") }
        listOf("\u270E" to s.msLockFix, "\u21BB" to s.msLockRecheck, "\u2913" to s.msLockDownload).forEach { (icon, line) ->
            Div({ classes("wl-ms-lock-item") }) {
                Span({ classes("wl-ms-lock-icon") }) { Text(icon) }
                Span { Text(line) }
            }
        }
        Div({ classes("wl-ms-lock-price") }) { Text(s.msLockPrice(price)) }
        Div({ classes("wl-ms-lock-actions") }) {
            Span({ classes("wl-ms-edit-btn", "primary"); onClick { onChoose() } }) {
                Text(if (isLoggedIn) s.msLockChoosePack else s.msLockSignIn)
            }
            Span({ classes("wl-ms-edit-link"); onClick { onClose() } }) { Text(s.msLockLater) }
        }
    }
}
